import React from 'react';
import { makeStyles, Paper } from '@material-ui/core';
import {
  BACKGROUND_DARK, BORDER_DARK, DASHBOARD_BOX_SHADOW, DASHBOARD_BOX_SHADOW_DARK, VERY_LIGHT_GREY_COLOR,
} from '../../../const/color-const';
import { FormattedMessage } from 'react-intl';
import NoDataAvailable from '../../common/charts/NoDataAvailable';
import { DASHBOARD_RADIUS } from '../../../const/chart-const';
import SalesBar from './SalesBar';

const useStyles = makeStyles(theme => ({
  overviewBoxWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2.5),
  },
  infoWrapper: {
    width: '100%',
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: DASHBOARD_RADIUS,
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing(1.5, 0, 0, 0),
    boxShadow: theme.palette.type === 'light' ? DASHBOARD_BOX_SHADOW : DASHBOARD_BOX_SHADOW_DARK,
    background: theme.palette.type === 'light' ? 'white' : BACKGROUND_DARK,
    border: theme.palette.type === 'light' ? `1px solid ${VERY_LIGHT_GREY_COLOR}` : `1px solid ${BORDER_DARK}`,
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(1),
    },
    [theme.breakpoints.up('sm')]: {
      maxWidth: '50%',
      marginBottom: 0,
    },
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(1.5, 0, 0, 0),
      maxWidth: '24%',
    },
  },
  contentTitle: {
    marginBottom: theme.spacing(1),
    padding: theme.spacing(0.5),
    fontSize: 15,
    fontFamily: 'Gilroy, sans-serif',
    fontWeight: 'bold',
  },
}));

const SALES_INCLUDING_VAT_PROPERTY = 'Nettoförsäljning inkl. moms';
const SALES_EXCLUDING_VAT_PROPERTY = 'Nettoförsäljning exkl. moms';
const SALES_VAT_PROPERTY = 'Nettoförsäljning, moms';

function getSalesSumIncludingVat(salesData) {
  return salesData && salesData.reduce((a, b) => Number(a) + Number(b[SALES_INCLUDING_VAT_PROPERTY] || 0), 0);
}

function getSalesSumExcludingVat(salesData) {
  return salesData && salesData.reduce((a, b) => Number(a) + Number(b[SALES_EXCLUDING_VAT_PROPERTY] || 0), 0);
}

function getSalesVATSum(salesData) {
  return salesData && salesData.reduce((a, b) => Number(a) + Number(b[SALES_VAT_PROPERTY] || 0), 0);
}

const SalesNumbersSummary = (props) => {
  const classes = useStyles();
  const {
    salesToday,
    salesYesterday,
    salesBeforeYesterday,
    salesThisWeek,
    salesLastWeek,
    salesPrevWeek,
  } = props;
  
  const today = salesToday && salesToday[SALES_INCLUDING_VAT_PROPERTY];
  const todayExclVat = salesToday && salesToday[SALES_EXCLUDING_VAT_PROPERTY];
  const todayVAT = salesToday && salesToday[SALES_VAT_PROPERTY];
  
  const yesterday = salesYesterday && salesYesterday[SALES_INCLUDING_VAT_PROPERTY];
  const yesterdayExclVat = yesterday && salesYesterday[SALES_EXCLUDING_VAT_PROPERTY];
  const yesterdayVAT = salesYesterday && salesYesterday[SALES_VAT_PROPERTY];
  
  const dayBeforeYesterday = salesBeforeYesterday && salesBeforeYesterday[SALES_INCLUDING_VAT_PROPERTY];
  
  const thisWeek = getSalesSumIncludingVat(salesThisWeek);
  const thisWeekExclVat = getSalesSumExcludingVat(salesThisWeek);
  const thisWeekVAT = getSalesVATSum(salesThisWeek);
  
  const lastWeek = getSalesSumIncludingVat(salesLastWeek);
  const lastWeekExclVat = getSalesSumExcludingVat(salesLastWeek);
  const lastWeekVAT = getSalesVATSum(salesLastWeek);
  
  const weekBeforeLast = getSalesSumIncludingVat(salesPrevWeek);
  
  const getPercentageDifference = (a, b) => {
    if (a === 0 || b === 0) {
      return;
    }
    let percent = (b - a) / a * 100;
    return Number(percent.toFixed(2));
  };
  
  const todayYesterdayDiff = getPercentageDifference(yesterday, today);
  const yesterdayDayBeforeYesterdayDiff = getPercentageDifference(dayBeforeYesterday, yesterday);
  const thisWeekLastWeekDiff = getPercentageDifference(lastWeek, thisWeek);
  const lastWeekPrevWeekDiff = getPercentageDifference(weekBeforeLast, lastWeek);
  
  return (
    <div className={classes.overviewBoxWrapper}>
      <Paper className={classes.infoWrapper}>
        <span className={classes.contentTitle}>
          <FormattedMessage
            id="page.dashboard.salesToday"
            defaultMessage="Sales today" />
        </span>
        {salesToday ? <SalesBar
            salesInclVat={today}
            salesExclVat={todayExclVat}
            vat={todayVAT}
            progressDiff={todayYesterdayDiff} />
          : <NoDataAvailable />}
      </Paper>
  
      <Paper className={classes.infoWrapper}>
        <span className={classes.contentTitle}>
          <FormattedMessage
            id="page.dashboard.salesYesterday"
            defaultMessage="Sales yesterday" />
        </span>
        {salesYesterday ? <SalesBar
            salesInclVat={yesterday}
            salesExclVat={yesterdayExclVat}
            vat={yesterdayVAT}
            progressDiff={yesterdayDayBeforeYesterdayDiff} />
          : <NoDataAvailable />}
      </Paper>
  
      <Paper className={classes.infoWrapper}>
        <span className={classes.contentTitle}>
          <FormattedMessage
            id="page.dashboard.salesThisWeek"
            defaultMessage="Sales this week"
          />
        </span>
        {thisWeek ? <SalesBar
            salesInclVat={thisWeek}
            salesExclVat={thisWeekExclVat}
            vat={thisWeekVAT}
            progressDiff={thisWeekLastWeekDiff} />
          : <NoDataAvailable />
        }
      </Paper>
  
      <Paper className={classes.infoWrapper}>
        <span className={classes.contentTitle}>
          <FormattedMessage
            id="page.dashboard.salesLastWeek"
            defaultMessage="Sales last week"
          />
        </span>
        {lastWeek ? <SalesBar
            salesInclVat={lastWeek}
            salesExclVat={lastWeekExclVat}
            vat={lastWeekVAT}
            progressDiff={lastWeekPrevWeekDiff} />
          : <NoDataAvailable />
        }
      </Paper>
    </div>
  );
};

export default SalesNumbersSummary;